import React, { useContext } from "react";
import { Box, Typography, Divider } from "@mui/material";
import {
	PersonOutline,
	LockClosedOutline,
	CalendarOutline,
	CardOutline,
	LogOutOutline,
	ListOutline,
	HeartOutline,
	MapOutline,
} from "react-ionicons";
import { CreatorContext } from "../../context/CreatorContext";
import { useTranslation } from "react-i18next";

import LogoBarber from "../../assets/showcases/barber-logo.png";
import LogoSalon from "../../assets/showcases/salon-logo.png";
import LogoTrainer from "../../assets/showcases/trainer-logo.png";
import LogoEcommerce from "../../assets/showcases/ecommerce-logo.png";
import LogoRestaurant from "../../assets/showcases/restaurant-logo.png";
import { UserContext } from "../../context/UserContext";
import chroma from "chroma-js";

const ShowcaseAccount = ({
	backgroundColor,
	showLogo,
	accountTitle,
	showIcons,
	showLoyalty,
}) => {
	const { creator } = useContext(CreatorContext);
	const { user } = useContext(UserContext);
	const { t } = useTranslation();

	const getLogo = (sector) => {
		switch (sector) {
			case "barber":
				return LogoBarber;

			case "salon":
				return LogoSalon;

			case "trainer":
				return LogoTrainer;

			case "ecommerce":
				return LogoEcommerce;

			case "restaurant":
				return LogoRestaurant;
			default:
				return {};
		}
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				backgroundColor: backgroundColor,
				minHeight: "100vh",
				padding: 2,
				fontSize: "8px",
				"& *": {
					fontFamily: creator.fontFamilyCap + "!important",
				},
			}}
		>
			{showLogo && (
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<img
						src={
							creator.appLogo
								? creator.appLogo
								: getLogo(user?.app?.appSubtype?.toLowerCase() ?? "barber")
						}
						alt={creator.appName}
						style={{ width: 80 }}
					/>
				</Box>
			)}
			<Box sx={{ marginBottom: 1, marginTop: 1 }}>
				<Typography variant="body1" sx={{ fontSize: "8px" }}>
					{accountTitle}
				</Typography>
				<Typography
					variant="body1"
					sx={{ fontSize: "12px", fontWeight: "bold" }}
				>
					Anna
				</Typography>
				<Typography variant="body2" sx={{ fontSize: "8px" }}>
					anna@example.com
				</Typography>
			</Box>
			<Typography
				variant="subtitle1"
				sx={{ fontSize: "7px", mb: 1, textTransform: "uppercase", mt: 1 }}
			>
				{t("account.myProfile")}
			</Typography>
			<Typography
				sx={{
					fontSize: "8px",
					display: "flex",
					alignItems: "center",
					gap: 1,
				}}
			>
				{showIcons && (
					<PersonOutline
						width="10px"
						style={{
							height: "auto",
							display: "flex",
							alignItems: "center",
							color: creator.themeMode === "Dark" ? "#ffffff" : "#000000",
						}}
					/>
				)}
				{t("account.changeName")}
			</Typography>
			<Divider
				sx={{
					marginTop: 1,
					marginBottom: 1,
					borderColor:
						creator.themeMode === "Dark"
							? chroma(creator.themeColor).brighten(1).hex()
							: chroma(creator.themeColor).darken(0.5).hex(),
				}}
			/>
			<Typography
				sx={{
					fontSize: "8px",
					display: "flex",
					alignItems: "center",
					gap: 1,
				}}
			>
				{showIcons && (
					<LockClosedOutline
						style={{
							height: "auto",
							display: "flex",
							alignItems: "center",
							color: creator.themeMode === "Dark" ? "#ffffff" : "#000000",
						}}
						width="10px"
					/>
				)}
				{t("account.changePassword")}
			</Typography>
			<Divider
				sx={{
					marginTop: 1,
					marginBottom: 1,
					borderColor:
						creator.themeMode === "Dark"
							? chroma(creator.themeColor).brighten(1).hex()
							: chroma(creator.themeColor).darken(0.5).hex(),
				}}
			/>
			{user?.app?.appType === "Ecommerce" && (
				<>
					<Typography
						sx={{
							fontSize: "8px",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						{showIcons && (
							<MapOutline
								style={{
									height: "auto",
									display: "flex",
									alignItems: "center",
									color: creator.themeMode === "Dark" ? "#ffffff" : "#000000",
								}}
								width="10px"
							/>
						)}
						{t("account.myAddresses")}
					</Typography>
					<Divider
						sx={{
							marginTop: 1,
							marginBottom: 1,
							borderColor:
								creator.themeMode === "Dark"
									? chroma(creator.themeColor).brighten(1).hex()
									: chroma(creator.themeColor).darken(0.5).hex(),
						}}
					/>
				</>
			)}
			<Typography
				variant="subtitle1"
				sx={{ fontSize: "7px", mb: 1, textTransform: "uppercase", mt: 1 }}
			>
				{t("account.appSection")}
			</Typography>
			{user?.app?.appType === "Services" && (
				<Typography
					sx={{
						fontSize: "8px",
						display: "flex",
						alignItems: "center",
						gap: 1,
					}}
				>
					{showIcons && (
						<CalendarOutline
							style={{
								height: "auto",
								display: "flex",
								alignItems: "center",
								color: creator.themeMode === "Dark" ? "#ffffff" : "#000000",
							}}
							width="10px"
						/>
					)}
					{t("account.yourAppointments")}
				</Typography>
			)}

			{user?.app?.appType === "Ecommerce" && (
				<Typography
					sx={{
						fontSize: "8px",
						display: "flex",
						alignItems: "center",
						gap: 1,
					}}
				>
					{showIcons && (
						<ListOutline
							style={{
								height: "auto",
								display: "flex",
								alignItems: "center",
								color: creator.themeMode === "Dark" ? "#ffffff" : "#000000",
							}}
							width="10px"
						/>
					)}
					{t("account.myorders")}
				</Typography>
			)}
			<Divider
				sx={{
					marginTop: 1,
					marginBottom: 1,
					borderColor:
						creator.themeMode === "Dark"
							? chroma(creator.themeColor).brighten(1).hex()
							: chroma(creator.themeColor).darken(0.5).hex(),
				}}
			/>
			{showLoyalty && (
				<>
					<Typography
						sx={{
							fontSize: "8px",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						{showIcons && (
							<CardOutline
								style={{
									height: "auto",
									display: "flex",
									alignItems: "center",
									color: creator.themeMode === "Dark" ? "#ffffff" : "#000000",
								}}
								width="10px"
							/>
						)}
						{t("account.loyaltyCard")}
					</Typography>
					<Divider
						sx={{
							marginTop: 1,
							marginBottom: 1,
							borderColor:
								creator.themeMode === "Dark"
									? chroma(creator.themeColor).brighten(1).hex()
									: chroma(creator.themeColor).darken(0.5).hex(),
						}}
					/>
				</>
			)}

			{user?.app?.appType === "Ecommerce" && (
				<>
					<Typography
						sx={{
							fontSize: "8px",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						{showIcons && (
							<HeartOutline
								style={{
									height: "auto",
									display: "flex",
									alignItems: "center",
									color: creator.themeMode === "Dark" ? "#ffffff" : "#000000",
								}}
								width="10px"
							/>
						)}
						{t("account.wishlist")}
					</Typography>
					<Divider
						sx={{
							marginTop: 1,
							marginBottom: 1,
							borderColor:
								creator.themeMode === "Dark"
									? chroma(creator.themeColor).brighten(1).hex()
									: chroma(creator.themeColor).darken(0.5).hex(),
						}}
					/>
				</>
			)}
			<Typography
				sx={{
					fontSize: "8px",
					display: "flex",
					alignItems: "center",
					gap: 1,
				}}
			>
				{showIcons && (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<LogOutOutline
							style={{
								height: "auto",
								display: "flex",
								alignItems: "center",
								color: creator.themeMode === "Dark" ? "#ffffff" : "#000000",
							}}
							width="10px"
						/>
					</Box>
				)}
				{t("account.logOut")}
			</Typography>
			<Divider
				sx={{
					marginTop: 1,
					marginBottom: 1,
					borderColor:
						creator.themeMode === "Dark"
							? chroma(creator.themeColor).brighten(1).hex()
							: chroma(creator.themeColor).darken(0.5).hex(),
				}}
			/>
		</Box>
	);
};

export default ShowcaseAccount;
